import { buildUpdateRow, shouldCompare, cleanPayloadRow } from '../utils/updateDiff';
import React, { useState, useMemo, useEffect } from 'react';
import ErrorBoundary from './ErrorBoundary';
import { categoriesAPI, transactionsAPI } from '../services/api';
import { localDateKey, parseCalendarDay } from '../utils/localDate';
import { formatApiError } from '../utils/errorMessage';
import { groupUnknownCategories, applyCategoryToName } from '../features/transactions/unknownCategories';
import { categoriesForTypes } from '../features/transactions/transactionTypes';
import { directionChange, typeDirectionConflict, normalizeType, transferAmount } from '../utils/updateDirection';

// Modal for updating existing transactions from CSV with comparison + review
const UpdateTransactionsModal = ({ isOpen, onClose, onUpdate, categories = [], onCategoriesUpdate }) => {
  // Step control: 1 = Upload, 2 = Map Columns, 3 = Review Changes, 4 = Verify Categories
  const [step, setStep] = useState(1);

  // Upload & Column Mapping
  const [updateFile, setUpdateFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    id: '',
    date: '',
    description: '',
    category: '',
    categoryId: '',
    amount: '',
    type: '',
    direction: '',
    reference: ''
  });

  // Review data - each transaction has changes, each change can be accepted/rejected individually
  // reviewItems: [{ 
  //   id, 
  //   description, 
  //   original: {...database values}, 
  //   updated: {...csv values},
  //   changes: [{ 
  //     field, label, 
  //     oldValue, newValue, 
  //     oldDisplay, newDisplay, 
  //     accepted: true/false (default true),
  //     needsAction (for categories)
  //   }] 
  // }]
  const [reviewItems, setReviewItems] = useState([]);

  // Category verification
  const [unmatchedCategories, setUnmatchedCategories] = useState([]); // Categories that need user selection
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', type: 'expense' });
  const [currentCategoryContext, setCurrentCategoryContext] = useState(null); // {transactionIndex, changeIndex}
  const [currentUnmatchedIndex, setCurrentUnmatchedIndex] = useState(null); // Index of unmatched category being edited

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Utils
  const formatAmount = (v) => (v === null || v === undefined || v === '' || isNaN(v) ? '—' : `$${Number(v).toFixed(2)}`);

  // Normalize date to YYYY-MM-DD format for comparison
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // A stored or ISO date keeps its calendar day; anything else is read in local time.
      const isoDay = /^(\d{4}-\d{2}-\d{2})(T|$)/.exec(String(dateStr).trim());
      if (isoDay) return isNaN(new Date(isoDay[1]).getTime()) ? null : isoDay[1];
      const date = parseCalendarDay(dateStr);
      if (isNaN(date.getTime())) return null;
      return localDateKey(date);
    } catch {
      return null;
    }
  };

  // Normalize value for comparison (trim strings, handle nulls)
  const normalizeValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') return value.trim();
    return value;
  };

  // Check if two values are actually different
  const valuesAreDifferent = (oldVal, newVal, fieldKey) => {
    // Normalize both values first
    let normOld = normalizeValue(oldVal);
    let normNew = normalizeValue(newVal);
    
    // Special handling for dates
    if (fieldKey === 'transaction_date') {
      const dateOld = normalizeDate(normOld);
      const dateNew = normalizeDate(normNew);
      // Both null/empty = no change
      if (!dateOld && !dateNew) return false;
      return dateOld !== dateNew;
    }
    
    // Special handling for numbers (amount)
    if (fieldKey === 'amount') {
      const numOld = normOld === null ? null : Number(normOld);
      const numNew = normNew === null ? null : Number(normNew);
      // Both null/empty = no change
      if (numOld === null && numNew === null) return false;
      if (numOld === null || numNew === null) return true;
      return Math.abs(numOld - numNew) > 0.001; // Allow tiny floating point differences
    }
    
    // For strings, both null/empty = no change
    if (normOld === null && normNew === null) return false;
    
    // Regular comparison with strict equality
    return normOld !== normNew;
  };

  // Auto-detect column mapping from headers
  const autoDetectColumns = (headers) => {
    const mapping = {
      id: '',
      date: '',
      description: '',
      category: '',
      categoryId: '',
      amount: '',
      type: '',
      direction: '',
      reference: ''
    };

    headers.forEach((header, index) => {
      const lower = String(header).toLowerCase().trim();
      
      // ID detection (REQUIRED for updates)
      if (lower === 'id' && !mapping.id) {
        mapping.id = index;
      }
      
      // Date detection
      if ((lower.includes('date') || lower === 'date') && !mapping.date) {
        mapping.date = index;
      }
      
      // Description detection
      if ((lower.includes('description') || lower === 'description') && !mapping.description) {
        mapping.description = index;
      }
      
      // Category name detection
      if (lower === 'category' && !mapping.category) {
        mapping.category = index;
      }
      
      // Category ID detection
      if ((lower === 'category id' || lower === 'categoryid') && !mapping.categoryId) {
        mapping.categoryId = index;
      }
      
      // Amount detection
      if ((lower === 'amount' || lower.includes('amount')) && !mapping.amount) {
        mapping.amount = index;
      }
      
      // Type detection
      if ((lower === 'type' || lower.includes('type')) && !mapping.type) {
        mapping.type = index;
      }
      
      // Direction detection (In/Out, stored transactions.direction)
      if (lower.includes('direction') && !mapping.direction) {
        mapping.direction = index;
      }
      
      // Reference detection
      if ((lower.includes('reference') || lower === 'reference') && !mapping.reference) {
        mapping.reference = index;
      }
    });

    return mapping;
  };

  // Fetch original transactions
  const fetchOriginalTransactions = async (ids) => {
    try {
      const results = await Promise.all(ids.map((id) => transactionsAPI.getById(id)));
      return results.map((r) => r.data);
    } catch (err) {
      console.error('Error fetching transactions:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      return [];
    }
  };

  // Build readable change list for one transaction
  const detectChanges = (original, updated) => {
    // Only check EDITABLE fields - exclude all ID fields
    const fields = [
      { key: 'transaction_date', label: 'Date' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', format: (v) => formatAmount(v) },
      { key: 'transaction_type', label: 'Type' },
      { key: 'reference_number', label: 'Reference' },
      { key: 'category_id', label: 'Category', isCategory: true }
    ];

    const changes = [];

    fields.forEach((field) => {
      let oldValue = original[field.key];
      let newValue = updated[field.key];

      // An unmapped column, or an empty cell in a required one, is never a change.
      if (!shouldCompare(updated, field.key)) return;

      // Special handling for categories
      if (field.isCategory) {
        let needsAction = false;
        const oldCat = categories.find((c) => c.id === oldValue);
        let oldDisplay = oldCat ? oldCat.name : (oldValue ? `ID: ${oldValue}` : 'None');
        let resolvedNewValue = newValue;
        let newDisplay = 'None';

        // If category_name is provided in CSV (not category_id), try to match it
        if (updated.category_name && !newValue) {
          const categoryName = String(updated.category_name).trim();
          const transType = updated.transaction_type || original.transaction_type;
          
          // Try exact match with type
          const matchExactType = categories.find(
            (c) => c.name?.toLowerCase() === categoryName.toLowerCase() && c.type === transType
          );
          // Try exact match any type
          const matchAny = categories.find((c) => c.name?.toLowerCase() === categoryName.toLowerCase());
          const match = matchExactType || matchAny;
          
          if (match) {
            resolvedNewValue = match.id;
            newDisplay = match.name;
          } else {
            // Category name doesn't match - needs user action
            newDisplay = categoryName;
            needsAction = true;
          }
        } else if (newValue) {
          // Category ID provided directly
          const newCat = categories.find((c) => c.id === newValue);
          newDisplay = newCat ? newCat.name : `ID: ${newValue}`;
          resolvedNewValue = newValue;
        }

        // Only add to changes if actually different
        if (valuesAreDifferent(oldValue, resolvedNewValue, field.key)) {
          changes.push({
            field: field.key,
            label: field.label,
            oldValue,
            newValue: resolvedNewValue,
            oldDisplay,
            newDisplay,
            needsAction,
            isCategory: true,
            unmatchedCategory: needsAction ? (updated.category_name || '') : null,
            transactionType: updated.transaction_type || original.transaction_type || 'expense',
          });
        }
      } else {
        // Non-category fields
        // Only add to changes if values are actually different
        if (valuesAreDifferent(oldValue, newValue, field.key)) {
          let oldDisplay = field.format ? field.format(oldValue) : (oldValue ?? 'None');
          let newDisplay = field.format ? field.format(newValue) : (newValue ?? 'None');
          
          // Special formatting for dates
          if (field.key === 'transaction_date') {
            oldDisplay = normalizeDate(oldValue) || 'None';
            newDisplay = normalizeDate(newValue) || 'None';
          }
          
          changes.push({ 
            field: field.key, 
            label: field.label, 
            oldValue, 
            newValue, 
            oldDisplay, 
            newDisplay, 
            needsAction: false 
          });
        }
      }
    });

    // Direction: only a mapped Direction column proposes a change (never derived from the type).
    const dirChange = directionChange(original, updated);
    if (dirChange) changes.push(dirChange);
    // A type that conflicts with the stored direction refuses the row (never auto-changed).
    const refusal = typeDirectionConflict(original, updated);
    if (refusal) changes.forEach((ch) => { ch.refused = refusal; });

    // A transfer's stored sign follows its direction; show a sign change.
    const signed = transferAmount(original, updated);
    if (signed !== null) {
      const existing = changes.find((c) => c.field === 'amount');
      if (existing) {
        existing.newValue = signed;
        existing.newDisplay = formatAmount(signed);
      } else {
        changes.push({ field: 'amount', label: 'Amount', oldValue: original.amount, newValue: signed,
          oldDisplay: formatAmount(original.amount), newDisplay: formatAmount(signed) + ' (sign follows direction)', needsAction: false });
      }
    }

    return changes;
  };

  // Handle reading the CSV and building comparison list
  const handleFileUpload = async () => {
    if (!updateFile) {
      alert('Please select a file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        
        if (lines.length < 2) {
          alert('File must contain a header row and at least one data row');
          return;
        }

        // Proper CSV parser that handles quoted fields with commas
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                // Escaped quote inside quoted field
                current += '"';
                i++; // Skip the next quote
              } else {
                // Toggle quote state
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              // Field separator (only when not inside quotes)
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          // Add the last field
          result.push(current.trim());
          return result;
        };

        // Parse header row
        const detectedHeaders = parseCSVLine(lines[0]);
        
        if (detectedHeaders.length === 0) {
          alert('No columns found in header row');
          return;
        }

        // Parse data rows (skip header)
        const dataRows = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          if (!line || !line.trim()) continue;
          
          const values = parseCSVLine(line);
          // Only add rows that have at least one non-empty value
          if (values.length > 0 && values.some(v => v !== '')) {
            dataRows.push(values);
          }
        }

        if (dataRows.length === 0) {
          alert('No data rows found in file');
          return;
        }

        // Auto-detect column mapping
        const initialMapping = autoDetectColumns(detectedHeaders);
        
        // Set state and move to Step 2 (Map Columns)
        setHeaders(detectedHeaders);
        setParsedRows(dataRows);
        setColumnMapping(initialMapping);
        setStep(2);
        
      } catch (err) {
        console.error('Failed to parse CSV:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        alert('Failed to parse CSV file. Please ensure it is properly formatted.');
      }
    };
    reader.readAsText(updateFile);
  };

  // Proceed from column mapping to review changes
  const handleProceedToReview = async () => {
    try {
      // Validate ID column is mapped (required for updates)
      if (!columnMapping.id && columnMapping.id !== 0) {
        alert('❌ Transaction ID column is REQUIRED for updates.\n\nPlease map the ID column to continue.');
        return;
      }

      // Parse rows directly using column mapping (no need to rebuild CSV text)
      const updatedRows = [];
      
      for (const row of parsedRows) {
        // Only mapped columns become fields; a row without an ID is skipped.
        const rowData = buildUpdateRow(row, columnMapping, normalizeType);
        if (rowData) updatedRows.push(rowData);
      }
      
      if (updatedRows.length === 0) {
        alert('No valid transactions found in file');
        return;
      }

      const ids = updatedRows.map((r) => r.id);
      const originals = await fetchOriginalTransactions(ids);

      const items = [];
      const unmatchedCats = [];

      updatedRows.forEach((upd) => {
        const orig = originals.find((o) => o.id === upd.id);
        if (!orig) return;
        
        const changes = detectChanges(orig, upd);
        if (changes.length > 0) {
          // Mark all changes as accepted by default
          changes.forEach(ch => {
            ch.accepted = !ch.refused; // User can toggle this (a refused row stays off)
            if (ch.isCategory && ch.needsAction && !ch.refused) {
              unmatchedCats.push({
                transactionId: upd.id,
                description: orig.description,
                categoryName: ch.unmatchedCategory,
                transactionType: ch.transactionType,
                changeIndex: items.length,
                fieldIndex: changes.indexOf(ch)
              });
            }
          });
          
          items.push({ 
            id: upd.id, 
            description: orig.description, 
            original: orig, 
            updated: upd, 
            changes 
          });
        }
      });

      if (items.length === 0) {
        alert('✓ No changes detected!\n\nAll transactions in the CSV file match the current database values.');
        return;
      }

      // Count change types for notification
      let totalChanges = 0;
      let dateChanges = 0;
      let amountChanges = 0;
      let categoryChanges = 0;
      let typeChanges = 0;
      let otherChanges = 0;
      
      items.forEach(item => {
        totalChanges += item.changes.length;
        item.changes.forEach(ch => {
          if (ch.field === 'transaction_date') dateChanges++;
          else if (ch.field === 'amount') amountChanges++;
          else if (ch.field === 'category_id') categoryChanges++;
          else if (ch.field === 'transaction_type') typeChanges++;
          else otherChanges++;
        });
      });

      // Show summary notification
      const summary = `📊 Changes Detected\n\n` +
        `Transactions with changes: ${items.length}\n` +
        `Total fields to update: ${totalChanges}\n\n` +
        `Breakdown:\n` +
        (dateChanges > 0 ? `  • Dates: ${dateChanges}\n` : '') +
        (amountChanges > 0 ? `  • Amounts: ${amountChanges}\n` : '') +
        (categoryChanges > 0 ? `  • Categories: ${categoryChanges}\n` : '') +
        (typeChanges > 0 ? `  • Types: ${typeChanges}\n` : '') +
        (otherChanges > 0 ? `  • Other fields: ${otherChanges}\n` : '') +
        (unmatchedCats.length > 0 ? `\n⚠️ ${unmatchedCats.length} unmatched categories need your review` : '') +
        `\n\n✓ All changes are PRE-SELECTED for update.\n✓ Review and uncheck any you want to SKIP.`;
      
      alert(summary);

      setReviewItems(items);
      setUnmatchedCategories(unmatchedCats);
      setStep(3); // Move to Review step
      
    } catch (err) {
      console.error('Failed to process mapped columns:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      alert('Failed to process mapped columns. Please check your column selections.');
    }
  };

  // One choice per unknown category name applies to every row that names it.
  const unknownCategoryGroups = useMemo(() => groupUnknownCategories(unmatchedCategories), [unmatchedCategories]);
  const handleCategorySelect = (nameKey, categoryId, list = categories) => {
    const { items, remaining } = applyCategoryToName(reviewItems, unmatchedCategories, nameKey, categoryId, list);
    setReviewItems(items);
    setUnmatchedCategories(remaining);
  };

  // Create new category and assign to current unmatched task
  const handleAddCategory = async () => {
    try {
      // Created only because the user asked to, from the Categories step.
      const response = await categoriesAPI.create(newCategory);
      await onCategoriesUpdate?.();
      if (currentUnmatchedIndex !== null) {
        handleCategorySelect(currentUnmatchedIndex, response.data.id, [...categories, response.data]);
      }
      setShowAddCategory(false);
      setNewCategory({ name: '', type: 'expense' });
      setCurrentUnmatchedIndex(null);
    } catch (error) {
      console.error('Failed to create category:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to create category'));
    }
  };

  // Stats for footer/summary
  const stats = useMemo(() => {
    const totalTx = reviewItems.length;
    let totalChanges = 0;
    let acceptedChanges = 0;
    let unresolvedAccepted = 0;
    let txWithAccepted = 0;

    reviewItems.forEach((item) => {
      totalChanges += item.changes.length;
      const accepted = item.changes.filter(c => c.accepted);
      if (accepted.length > 0) txWithAccepted++;
      acceptedChanges += accepted.length;
      unresolvedAccepted += accepted.filter((c) => c.needsAction).length;
    });

    return { totalTx, txWithAccepted, totalChanges, acceptedChanges, unresolvedAccepted };
  }, [reviewItems]);

  // Build final payload and submit
  const handleUpdate = () => {
    if (stats.unresolvedAccepted > 0) {
      alert('⚠️ Cannot proceed\n\nPlease resolve all category selections for the changes you accepted.');
      return;
    }

    if (stats.acceptedChanges === 0) {
      alert('⚠️ No changes selected\n\nPlease accept at least one change before updating.');
      return;
    }

    // Show confirmation with summary
    const confirmMsg = `📋 Ready to Update\n\n` +
      `Transactions to update: ${stats.txWithAccepted}\n` +
      `Total changes to apply: ${stats.acceptedChanges}\n\n` +
      `⚠️ This will permanently modify your transaction data.\n\n` +
      `Continue with update?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    const payload = [];
    const updateSummary = {
      transactions: 0,
      fields: 0,
      byField: {}
    };

    reviewItems.forEach((item) => {
      const acceptedChanges = item.changes.filter(ch => ch.accepted && !ch.needsAction && !ch.refused);
      if (acceptedChanges.length === 0) return;
      
      const base = { id: item.updated.id };
      acceptedChanges.forEach((ch) => {
        base[ch.field] = ch.newValue;
        updateSummary.fields++;
        updateSummary.byField[ch.label] = (updateSummary.byField[ch.label] || 0) + 1;
      });
      // Protect fields the backend updates directly without COALESCE
      if (base.state_registration_id === undefined) base.state_registration_id = item.original.state_registration_id;
      if (base.reference_number === undefined) base.reference_number = item.original.reference_number;
      payload.push(cleanPayloadRow(base));
      updateSummary.transactions++;
    });

    if (payload.length === 0) {
      alert('⚠️ Nothing selected to update.');
      return;
    }

    onUpdate(payload);
    handleClose();
  };

  const handleClose = () => {
    try {
      setStep(1);
      setUpdateFile(null);
      setReviewItems([]);
      setUnmatchedCategories([]);
      setShowAddCategory(false);
      setNewCategory({ name: '', type: 'expense' });
      setCurrentUnmatchedIndex(null);
      setCurrentCategoryContext(null);
    } catch (error) {
      console.error('Error resetting modal state:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    } finally {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Render
  return (
    <ErrorBoundary>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          // Close modal if clicking the backdrop
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <ErrorBoundary>
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Update Transactions from Excel</h2>
              <button 
                onClick={handleClose} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </ErrorBoundary>

          {/* Steps */}
          <ErrorBoundary>
            <div className="flex items-center justify-center p-4 bg-gray-50 border-b overflow-x-auto">
              <div className="flex items-center space-x-2">
                <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
                  <span className="ml-2 font-medium text-sm">Upload</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-300"/>
                <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
                  <span className="ml-2 font-medium text-sm">Review</span>
                </div>
                {unmatchedCategories.length > 0 && (
                  <>
                    <div className="w-12 h-0.5 bg-gray-300"/>
                    <div className={`flex items-center ${step >= 3 ? 'text-orange-600' : 'text-gray-400'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-orange-600 text-white' : 'bg-gray-200'}`}>3</div>
                      <span className="ml-2 font-medium text-sm">Categories</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </ErrorBoundary>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Upload */}
            {step === 1 && (
              <ErrorBoundary>
                <div className="max-w-2xl mx-auto space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                      <li>Export your transactions using the Export button.</li>
                      <li>Edit the CSV in Excel with updated values.</li>
                      <li>Upload the file here to review all changes before applying.</li>
                    </ol>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select CSV file:</label>
                    <input type="file" accept=".csv" onChange={(e) => setUpdateFile(e.target.files[0])} className="w-full border rounded px-3 py-2" />
                  </div>
                </div>
              </ErrorBoundary>
            )}

            {/* Step 2: Map Columns */}
            {step === 2 && (
              <ErrorBoundary>
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📋 Map CSV Columns to Transaction Fields</h4>
                    <p className="text-sm text-blue-800">
                      Match the columns in your CSV file to the transaction fields below. 
                      The <strong>Transaction ID</strong> column is <span className="text-red-700 font-bold">REQUIRED</span> for updates.
                    </p>
                  </div>

                  <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      
                      {/* Transaction ID - Required */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-900">
                          Transaction ID <span className="text-red-600 text-xl">*</span>
                        </label>
                        <select
                          value={columnMapping.id !== null && columnMapping.id !== undefined ? columnMapping.id : ''}
                          onChange={(e) => setColumnMapping({ ...columnMapping, id: e.target.value === '' ? null : e.target.value })}
                          className={`w-full border-2 rounded-lg px-4 py-3 text-base ${!columnMapping.id && columnMapping.id !== 0 ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'}`}
                        >
                          <option value="">-- Select CSV Column --</option>
                          {headers.map((h, i) => (
                            <option key={i} value={i}>{h}</option>
                          ))}
                        </select>
                        {(!columnMapping.id && columnMapping.id !== 0) && (
                          <p className="text-xs text-red-700 font-semibold mt-1">⚠️ Required</p>
                        )}
                      </div>

                      {/* Optional columns */}
                      {[['date', 'Date'], ['description', 'Description'], ['amount', 'Amount'], ['category', 'Category Name'], ['categoryId', 'Category ID'],
                        ['type', 'Type'], ['direction', 'Direction (In/Out)', 'map-direction'], ['reference', 'Reference']].map(([field, label, testId]) => (
                        <div key={field}>
                          <label className="block text-sm font-semibold mb-2 text-gray-700">{label}</label>
                          <select
                            data-testid={testId}
                            value={columnMapping[field] !== null && columnMapping[field] !== undefined ? columnMapping[field] : ''}
                            onChange={(e) => setColumnMapping({ ...columnMapping, [field]: e.target.value === '' ? null : e.target.value })}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-base bg-white"
                          >
                            <option value="">-- Not Mapped --</option>
                            {headers.map((h, i) => (<option key={i} value={i}>{h}</option>))}
                          </select>
                        </div>
                      ))}

                    </div>
                  </div>

                  {/* Sample Data Preview */}
                  {parsedRows.length > 0 && (
                    <div className="bg-white border-2 border-blue-300 rounded-lg p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-2xl">👁️</span>
                        <h5 className="font-bold text-gray-900 text-lg">Sample Data Preview</h5>
                        <span className="text-sm text-gray-600">(First 3 rows from your CSV)</span>
                      </div>
                      <div className="overflow-x-auto rounded-lg border-2 border-gray-300">
                        <table className="w-full text-sm">
                          <thead className="bg-blue-100 border-b-2 border-blue-300">
                            <tr>
                              {headers.map((h, i) => (
                                <th key={i} className="px-4 py-3 text-left font-bold text-gray-900 whitespace-nowrap border-r border-blue-200 last:border-r-0">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {parsedRows.slice(0, 3).map((row, rowIdx) => (
                              <tr key={rowIdx} className="border-b border-gray-200 hover:bg-gray-50">
                                {headers.map((h, cellIdx) => (
                                  <td key={cellIdx} className="px-4 py-3 border-r border-gray-200 last:border-r-0">
                                    <div className="max-w-xs overflow-hidden" title={row[cellIdx] || ''}>
                                      {row[cellIdx] || <span className="text-gray-400 italic">(empty)</span>}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        💡 Verify that data appears in the correct columns before continuing.
                      </p>
                    </div>
                  )}
                </div>
              </ErrorBoundary>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <ErrorBoundary>
                <div className="space-y-6">
                  {/* Summary */}
                  <ErrorBoundary>
                    <div className="grid grid-cols-4 gap-4">
                      <ErrorBoundary>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="text-sm text-blue-600 font-medium">Transactions</div>
                          <div className="text-2xl font-bold text-blue-900">{reviewItems.length}</div>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="text-sm text-green-600 font-medium">Accepted Changes</div>
                          <div className="text-2xl font-bold text-green-900">{stats.acceptedChanges}</div>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <div className="text-sm text-purple-600 font-medium">Total Changes</div>
                          <div className="text-2xl font-bold text-purple-900">{stats.totalChanges}</div>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className={`rounded-lg p-4 ${stats.unresolvedAccepted > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                          <div className={`text-sm font-medium ${stats.unresolvedAccepted > 0 ? 'text-orange-700' : 'text-gray-600'}`}>Unresolved</div>
                          <div className={`text-2xl font-bold ${stats.unresolvedAccepted > 0 ? 'text-orange-900' : 'text-gray-900'}`}>{stats.unresolvedAccepted}</div>
                        </div>
                      </ErrorBoundary>
                    </div>
                  </ErrorBoundary>

                  {/* Instructions */}
                  <ErrorBoundary>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="font-medium text-blue-900 mb-2">📋 Review Each Change</div>
                      <div className="text-sm text-blue-800 space-y-1">
                        <div>• <strong>Check</strong> the changes you want to apply</div>
                        <div>• <strong>Uncheck</strong> any changes you want to skip</div>
                        <div>• <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">DATABASE VALUE</span> → <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">CSV VALUE</span></div>
                      </div>
                    </div>
                  </ErrorBoundary>

                  {/* Changes list */}
                  <div className="space-y-4">
                    {reviewItems.map((item, itemIdx) => {
                      try {
                        const acceptedCount = item.changes.filter(ch => ch.accepted).length;
                        const allAccepted = acceptedCount === item.changes.length;
                        const someAccepted = acceptedCount > 0 && acceptedCount < item.changes.length;
                        
                        return (
                          <ErrorBoundary key={item.id || `review-item-${itemIdx}`}>
                            <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                              {/* Transaction Header */}
                              <ErrorBoundary>
                                <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                                  <ErrorBoundary>
                                    <div className="flex-1">
                                      <div className="font-semibold text-gray-900">
                                        {item.description || '(no description)'} 
                                        <span className="text-gray-500 text-sm ml-2">(ID: {item.id})</span>
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        {acceptedCount} of {item.changes.length} changes accepted
                                      </div>
                                      {item.changes[0]?.refused && <div className="text-sm text-red-700 font-medium mt-1" data-testid="row-refused">Refused: {item.changes[0].refused}</div>}
                                    </div>
                                  </ErrorBoundary>
                                  <ErrorBoundary>
                                    <div className="flex items-center gap-3">
                                      <button
                                        onClick={() => {
                                          try {
                                            const items = [...reviewItems];
                                            items[itemIdx].changes.forEach(ch => { ch.accepted = !ch.refused; });
                                            setReviewItems(items);
                                          } catch (error) {
                                            console.error('Error accepting all changes:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                                            alert('Error accepting changes. Please try again.');
                                          }
                                        }}
                                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                      >
                                        Accept All
                                      </button>
                                      <button
                                        onClick={() => {
                                          try {
                                            const items = [...reviewItems];
                                            items[itemIdx].changes.forEach(ch => ch.accepted = false);
                                            setReviewItems(items);
                                          } catch (error) {
                                            console.error('Error rejecting all changes:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                                            alert('Error rejecting changes. Please try again.');
                                          }
                                        }}
                                        className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                      >
                                        Reject All
                                      </button>
                                    </div>
                                  </ErrorBoundary>
                                </div>
                              </ErrorBoundary>

                              {/* Individual Changes */}
                              <ErrorBoundary>
                                <div className="p-4 space-y-3">
                                  {item.changes.map((ch, changeIdx) => (
                                    <ErrorBoundary key={changeIdx}>
                                      <div 
                                        className={`border-2 rounded-lg p-3 ${
                                          ch.needsAction 
                                            ? 'border-orange-400 bg-orange-50' 
                                            : ch.accepted 
                                              ? 'border-green-300 bg-green-50' 
                                              : 'border-gray-200 bg-white'
                                        }`}
                                      >
                                        <div className="flex items-start gap-3">
                                          {/* Checkbox */}
                                          <ErrorBoundary>
                                            <input
                                              type="checkbox"
                                              className="mt-1 h-5 w-5 cursor-pointer"
                                              checked={ch.accepted} disabled={Boolean(ch.refused)}
                                              onChange={(e) => {
                                                try {
                                                  const items = [...reviewItems];
                                                  items[itemIdx].changes[changeIdx].accepted = e.target.checked;
                                                  setReviewItems(items);
                                                } catch (error) {
                                                  console.error('Error toggling change acceptance:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                                                  alert('Error updating selection. Please try again.');
                                                }
                                              }}
                                              disabled={ch.needsAction}
                                            />
                                          </ErrorBoundary>
                                          
                                          {/* Change Details */}
                                          <ErrorBoundary>
                                            <div className="flex-1">
                                              <div className="font-medium text-gray-700 mb-2">{ch.label}</div>
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <ErrorBoundary>
                                                  <div className="text-sm">
                                                    <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-mono line-through">
                                                      {String(ch.oldDisplay)}
                                                    </span>
                                                  </div>
                                                </ErrorBoundary>
                                                <span className="text-gray-500">→</span>
                                                <ErrorBoundary>
                                                  <div className="text-sm">
                                                    <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-mono font-semibold">
                                                      {String(ch.newDisplay)}
                                                    </span>
                                                  </div>
                                                </ErrorBoundary>
                                                {ch.needsAction && (
                                                  <ErrorBoundary>
                                                    <span className="px-2 py-1 rounded bg-orange-200 text-orange-900 text-xs font-medium">
                                                      ⚠️ Needs Category Selection
                                                    </span>
                                                  </ErrorBoundary>
                                                )}
                                              </div>
                                            </div>
                                          </ErrorBoundary>
                                        </div>
                                      </div>
                                    </ErrorBoundary>
                                  ))}
                                </div>
                              </ErrorBoundary>
                            </div>
                          </ErrorBoundary>
                        );
                      } catch (error) {
                        console.error('Error rendering transaction item:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                        return (
                          <ErrorBoundary key={item?.id || `error-item-${itemIdx}`}>
                            <div className="border-2 border-red-300 rounded-lg p-4 bg-red-50">
                              <div className="text-red-800 font-medium">Error displaying transaction</div>
                              <div className="text-xs text-red-600 mt-1">Transaction ID: {item?.id || 'unknown'}</div>
                            </div>
                          </ErrorBoundary>
                        );
                      }
                    })}
                  </div>
                </div>
              </ErrorBoundary>
            )}

            {/* Step 4: Verify Categories */}
            {step === 4 && (
              <ErrorBoundary>
                <div className="space-y-4">
                  <ErrorBoundary>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        {unknownCategoryGroups.length} categor{unknownCategoryGroups.length === 1 ? 'y' : 'ies'} in the file {unknownCategoryGroups.length === 1 ? "doesn't" : "don't"} exist yet ({unmatchedCategories.length} row(s)). Map each name to an existing category or create it. The choice applies to every row with that name.
                      </p>
                    </div>
                  </ErrorBoundary>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {unknownCategoryGroups.map((group) => (
                      <ErrorBoundary key={`unknown-category-${group.key}`}>
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <div className="mb-2">
                            <div className="font-semibold text-gray-900">"{group.name}"</div>
                            <div className="text-sm text-gray-600">
                              {group.count} row(s): {group.tasks.slice(0, 3).map((t) => t.description).filter(Boolean).join(', ')}{group.count > 3 ? ', ...' : ''}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <select
                              aria-label={`Category for ${group.name}`}
                              value=""
                              onChange={(e) => { if (e.target.value) handleCategorySelect(group.key, parseInt(e.target.value, 10)); }}
                              className="flex-1 border rounded px-3 py-2 text-sm"
                            >
                              <option value="">Map to an existing category</option>
                              {categoriesForTypes(categories, group.transactionTypes)
                                .map((cat) => (
                                  <option key={cat.id} value={cat.id}>{cat.name} ({cat.type})</option>
                                ))}
                            </select>
                            <button
                              onClick={() => {
                                setCurrentUnmatchedIndex(group.key);
                                setNewCategory({ name: group.name, type: group.transactionType || 'expense' });
                                setShowAddCategory(true);
                              }}
                              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                            >
                              + Create "{group.name}"
                            </button>
                          </div>
                        </div>
                      </ErrorBoundary>
                    ))}
                  </div>
                </div>
              </ErrorBoundary>
            )}
          </div>

          {/* Footer */}
          <ErrorBoundary>
            <div className="p-6 border-t flex items-center justify-between">
              <ErrorBoundary>
                {step > 1 ? (
                  <div className="text-sm text-gray-600">
                    Transactions: {stats.txWithAccepted} • Accepted changes: {stats.acceptedChanges} of {stats.totalChanges} • Unresolved: {stats.unresolvedAccepted}
                  </div>
                ) : <span />}
              </ErrorBoundary>

              <ErrorBoundary>
                <div className="flex gap-3">
                  <button 
                    onClick={() => {
                      try {
                        handleClose();
                      } catch (error) {
                        console.error('Error closing modal:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                        alert('Error closing modal. Please refresh the page.');
                      }
                    }} 
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {step === 1 && (
                    <button 
                      onClick={() => {
                        try {
                          handleFileUpload();
                        } catch (error) {
                          console.error('Error processing file:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                          alert('Error processing file. Please try again.');
                        }
                      }} 
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700" 
                      disabled={!updateFile}
                    >
                      Process File
                    </button>
                  )}
                  {step === 2 && (
                    <button 
                      onClick={() => {
                        try {
                          handleProceedToReview();
                        } catch (error) {
                          console.error('Error proceeding to review:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                          alert('Error proceeding to review. Please try again.');
                        }
                      }} 
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      disabled={!columnMapping.id && columnMapping.id !== 0}
                    >
                      Continue to Review
                    </button>
                  )}
                  {step === 3 && (
                    <>
                      {unmatchedCategories.length > 0 && (
                        <button 
                          onClick={() => {
                            try {
                              setStep(4);
                            } catch (error) {
                              console.error('Error navigating to category verification:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                              alert('Error navigating. Please try again.');
                            }
                          }} 
                          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                        >
                          Verify Categories
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          try {
                            handleUpdate();
                          } catch (error) {
                            console.error('Error updating transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                            alert('Error updating transactions. Please try again.');
                          }
                        }} 
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700" 
                        disabled={stats.acceptedChanges === 0 || stats.unresolvedAccepted > 0}
                      >
                        Update Transactions
                      </button>
                    </>
                  )}
                  {step === 4 && (
                    <button 
                      onClick={() => {
                        try {
                          handleUpdate();
                        } catch (error) {
                          console.error('Error updating transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
                          alert('Error updating transactions. Please try again.');
                        }
                      }} 
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700" 
                      disabled={unmatchedCategories.length > 0}
                    >
                      Update Transactions
                    </button>
                  )}
                </div>
              </ErrorBoundary>
            </div>
          </ErrorBoundary>

          {/* Add Category Modal */}
          {showAddCategory && (
            <ErrorBoundary>
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h4 className="text-lg font-bold mb-4">Add New Category</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category Name</label>
                      <input type="text" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Type</label>
                      <select value={newCategory.type} onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })} className="w-full border rounded px-3 py-2">
                        <option value="expense">Expense</option>
                        <option value="income">Income</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button onClick={() => { setShowAddCategory(false); setNewCategory({ name: '', type: 'expense' }); setCurrentUnmatchedIndex(null); }} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                      <button onClick={handleAddCategory} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700" disabled={!newCategory.name.trim()}>Create Category</button>
                    </div>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default UpdateTransactionsModal;
